/**
 * Guide Signup Success Page
 *
 * Shown after guide completes signup flow
 * Extracted from GuideSignupFlow.tsx mockup (CompletionStage)
 *
 * Epic: MVP 1.2.1 - Guide Signup Flow
 * Location: src/pages/GuideSignupSuccess.jsx
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, Phone, Mail } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { getCachedPreLaunchEnabled } from '../../utils/preLaunchConfigCache';

/**
 * GuideSignupSuccess Component
 */
const GuideSignupSuccess = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {!getCachedPreLaunchEnabled() && <Header />}

      <main className="max-w-2xl mx-auto px-4 py-12 text-center">
        {/* Success Icon */}
        <div className="inline-flex items-center justify-center w-24 h-24 bg-emerald-100 rounded-full mb-6">
          <Check className="w-12 h-12 text-emerald-600" />
        </div>

        {/* Heading */}
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Welcome to OutdoorGuides!
        </h1>

        {/* Subheading */}
        <p className="text-xl text-gray-600 mb-8">
          Your profile is under review and will be live within 24 hours
        </p>

        {/* What Happens Next */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-8 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            What happens next?
          </h3>
          <div className="space-y-4 text-left max-w-md mx-auto">
            {/* Step 1 */}
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold mr-3">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Verification (2-24 hours)</h4>
                <p className="text-sm text-gray-600">We&apos;ll verify your ID and certifications</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold mr-3">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Profile Goes Live</h4>
                <p className="text-sm text-gray-600">You&apos;ll appear in customer searches immediately</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold mr-3">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Start Receiving Bookings</h4>
                <p className="text-sm text-gray-600">We&apos;ll notify you of inquiries via SMS and email</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/dashboard"
            className="inline-flex items-center justify-center px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
          >
            Go to Dashboard
            <ChevronRight className="ml-2 w-4 h-4" />
          </Link>

          <Link
            to="/help"
            className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg border-2 border-gray-300 hover:border-gray-400 transition-colors"
          >
            Get Help
          </Link>
        </div>

        {/* Contact Information */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-4">
            Questions? We&apos;re here to help!
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="tel:555-123-4567" className="text-emerald-600 hover:text-emerald-700 flex items-center">
              <Phone className="w-4 h-4 mr-1.5" />
              (555) 123-4567
            </a>
            <a href="mailto:guides@outdoorguides.com" className="text-emerald-600 hover:text-emerald-700 flex items-center">
              <Mail className="w-4 h-4 mr-1.5" />
              guides@outdoorguides.com
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GuideSignupSuccess;
